import type { FC, ReactNode } from 'react'
import type { CountryCode } from 'libphonenumber-js'

import { useMemo } from 'react'
import { CircleX } from 'lucide-react'
import { useMounted } from '@/hooks/use-mounted'
import { useI18n } from '@/i18n/context'
import { COUNTRY_CODES, POPULAR_COUNTRIES, dialCode } from '@/lib/validation'
import { SelectInput, TextInput } from '@/components/primitives/field'
import { cn } from '@/lib/utils'

const useCountryNames = (): ((code: string) => string) | null => {
    const mounted = useMounted()
    const { locale } = useI18n()

    return useMemo(() => {
        if (!mounted) return null

        try {
            const names = new Intl.DisplayNames([locale], { type: 'region' })

            return (code: string) => names.of(code) ?? code
        }
        catch {
            return (code: string) => code
        }
    }, [mounted, locale])
}

const useSortedCountries = (nameOf: ((code: string) => string) | null): Array<CountryCode> => {
    const { locale } = useI18n()

    return useMemo(() => {
        const rest = COUNTRY_CODES.filter(code => !POPULAR_COUNTRIES.includes(code))

        if (!nameOf) return [...POPULAR_COUNTRIES, ...rest]

        return [...POPULAR_COUNTRIES, ...rest.sort((a, b) => nameOf(a).localeCompare(nameOf(b), locale))]
    }, [nameOf, locale])
}

export const CountryName: FC<{ code: string }> = ({ code }): ReactNode => {
    const nameOf = useCountryNames()

    return <>{nameOf && code ? nameOf(code) : code}</>
}

interface NationalitySelectProps {
    id: string
    value: string
    onChange: (value: string) => void
    invalid?: boolean
    placeholder: string
}

export const NationalitySelect: FC<NationalitySelectProps> = ({ id, value, onChange, invalid, placeholder }): ReactNode => {
    const nameOf = useCountryNames()
    const countries = useSortedCountries(nameOf)

    return (
        <SelectInput id={id} value={value} invalid={invalid} onChange={event => onChange(event.target.value)} autoComplete='country'>
            <option value=''>{placeholder}</option>

            {countries.map(code => (
                <option key={code} value={code}>
                    {nameOf ? nameOf(code) : code}
                </option>
            ))}
        </SelectInput>
    )
}

interface PhoneFieldProps {
    id: string
    country: string
    number: string
    onChange: (value: { country: string, number: string }) => void
    invalid?: boolean
    codeLabel: string
    codePlaceholder: string
    numberPlaceholder: string
}

export const PhoneField: FC<PhoneFieldProps> = ({ id, country, number, onChange, invalid, codeLabel, codePlaceholder, numberPlaceholder }): ReactNode => {
    const nameOf = useCountryNames()
    const countries = useSortedCountries(nameOf)
    const selected = country ? `${country} ${dialCode(country as CountryCode)}` : codePlaceholder

    return (
        <div className='flex gap-2' dir='ltr'>
            <div className='relative w-[118px] shrink-0'>
                <div
                    aria-hidden='true'
                    className={cn(
                        'pointer-events-none flex h-12 items-center justify-between rounded-xl border bg-[#0c1714] px-3.5 text-[15px]',
                        invalid && !country ? 'border-danger/80 ring-1 ring-danger/35' : 'border-input',
                        country ? 'text-foreground' : 'text-muted-foreground/70'
                    )}
                >
                    <span className='truncate font-medium'>{selected}</span>
                    <svg viewBox='0 0 24 24' className='size-4 shrink-0 text-gold' fill='none' stroke='currentColor' strokeWidth='2'>
                        <path d='m6 9 6 6 6-6' />
                    </svg>
                </div>

                <select
                    id={`${id}-country`}
                    aria-label={codeLabel}
                    value={country}
                    onChange={event => onChange({ country: event.target.value, number })}
                    className='absolute inset-0 h-12 w-full cursor-pointer opacity-0'
                    autoComplete='tel-country-code'
                >
                    <option value=''>{codePlaceholder}</option>

                    {countries.map(code => (
                        <option key={code} value={code}>
                            {nameOf ? `${nameOf(code)} (${dialCode(code)})` : `${code} ${dialCode(code)}`}
                        </option>
                    ))}
                </select>
            </div>

            <div className='relative min-w-0 flex-1'>
                <TextInput
                    id={id}
                    type='tel'
                    inputMode='tel'
                    autoComplete='tel-national'
                    value={number}
                    invalid={invalid}
                    placeholder={numberPlaceholder}
                    onChange={event => onChange({ country, number: event.target.value })}
                />

                {invalid && <CircleX className='pointer-events-none absolute right-3.5 top-1/2 size-5 -translate-y-1/2 text-danger' aria-hidden='true' />}
            </div>
        </div>
    )
}
